import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ClipboardPlus, Loader2, UserRound } from "lucide-react";
import {
  cdmMenuItems,
  menuItems,
} from "pages/global/ReportsPage/components/ReportsNavbar/utils/menuItems";
import { reportTypeToTableTitle } from "pages/global/ReportsPage/components/ReportsNavbar/utils/reportTypeToTableTitle";

import { SendSMSModal } from "components/SendSMSModal/SendSMSModal";
import { Button } from "components/ui/Button";
import { DataTable } from "components/ui/DataTable/DataTable";
import { useDataTableSelection } from "components/ui/DataTable/hooks/useDataTableSelection";
import { pathGenerators } from "utils/routes";

import { CDM_FINDER_REPORT_TYPE } from "../../constants/cdmFinder";
import { hasCdmFinderCriteria } from "../../utils/buildCdmFinderParams";
import { getColumns } from "../../utils/getColumns";
import styles from "./ReportsTable.module.css";

const getReportIcon = (reportType) => {
  const allItems = [...menuItems, ...cdmMenuItems];
  const item =
    allItems.find((m) => m.url.includes(reportType ?? "")) ?? menuItems[0];
  const Icon = item.src;
  return <Icon size={20} strokeWidth={2} />;
};

const ROWS_OPTIONS = [10, 20, 50, "All"];

const SELECTABLE_TYPES = new Set([
  "immunisations",
  "accounting",
  "appointments",
]);

const hasSelectionForType = (reportType) =>
  !reportType || SELECTABLE_TYPES.has(reportType);

const getRowId = (reportType) => (row) =>
  reportType === "appointments"
    ? String(row.appointmentId ?? row.patientId)
    : String(row.patientId);

const GENDER_OPTIONS = [
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
  { value: "other", label: "Other" },
];

// The CDM finder / recall / register reports have their own Name + Gender
// controls in their own filters, so they don't use the table's built-in
// "Filter by → Gender" dropdown or the "Search by last name" box.
const CDM_SELF_FILTERED_TYPES = new Set([
  CDM_FINDER_REPORT_TYPE,
  "cdm-recall",
  "cdm-register",
]);

const NO_GENDER_TYPES = new Set([
  "appointments",
  "medico-legal",
  "patient-merges",
  ...CDM_SELF_FILTERED_TYPES,
]);

// CDM reports whose rows carry a patientId — each row gets "Open patient" /
// "Open patient CDM" actions.
const PATIENT_ROW_ACTION_TYPES = new Set([
  CDM_FINDER_REPORT_TYPE,
  "cdm-recall",
  "cdm-register",
]);

// The default patients report and the CDM finder both filter on `gender`;
// the other report types use `patientGender`.
const PLAIN_GENDER_TYPES = new Set([CDM_FINDER_REPORT_TYPE]);

// Reports the backend can render as PDF as well as Excel (KIN-1409 G13); the GP
// picks the format.
const PDF_EXPORT_TYPES = new Set([CDM_FINDER_REPORT_TYPE, "cdm-register"]);

const getGenderField = (reportType) =>
  !reportType || PLAIN_GENDER_TYPES.has(reportType) ? "gender" : "patientGender";

const getGenderFiltersConfig = (reportType) => {
  if (NO_GENDER_TYPES.has(reportType)) return [];
  return [
    {
      id: getGenderField(reportType),
      label: "Gender",
      type: "single",
      options: GENDER_OPTIONS,
    },
  ];
};

export const ReportsTable = ({
  reportType,
  items,
  isLoading,
  totalRecords,
  page,
  onPageChange,
  rows,
  onRowsChange,
  onExport,
  isExporting,
  filters,
  onFilterChange,
}) => {
  const [smsModalOpen, setSmsModalOpen] = useState(false);
  const [smsItems, setSmsItems] = useState([]);
  const navigate = useNavigate();

  const rowIdFn = getRowId(reportType);
  const selection = useDataTableSelection(rowIdFn);
  const selectable = hasSelectionForType(reportType);

  const isFinder = reportType === CDM_FINDER_REPORT_TYPE;

  // The current patient is carried in the URL path (KIN-1258), so navigating to
  // the id-bearing route is all that's needed — no Redux / storage write.
  const goToPatient = (row, buildPath) => {
    navigate(buildPath(row.patientId));
  };

  const rowActions = PATIENT_ROW_ACTION_TYPES.has(reportType)
    ? [
        {
          label: "Open patient",
          icon: UserRound,
          onClick: (row) => goToPatient(row, pathGenerators.patient.details),
        },
        {
          label: "Open patient CDM",
          icon: ClipboardPlus,
          onClick: (row) => goToPatient(row, pathGenerators.patient.cdm),
        },
      ]
    : [];

  const finderIdle = isFinder && !hasCdmFinderCriteria(filters);

  const hasLastName =
    reportType !== "appointments" && !CDM_SELF_FILTERED_TYPES.has(reportType);
  const tableSearch = hasLastName
    ? {
        value: filters?.patientLastname || "",
        onChange: (val) => onFilterChange("patientLastname", val),
        placeholder: "Search by last name",
      }
    : undefined;

  const genderField = getGenderField(reportType);
  const filtersConfig = getGenderFiltersConfig(reportType);
  const tableFilters =
    filtersConfig.length > 0
      ? {
          value: { [genderField]: filters?.[genderField] || null },
          onChange: (id, val) => onFilterChange(id, val || ""),
        }
      : undefined;

  const effectiveRowsPerPage =
    rows === "All" ? Math.max(totalRecords || 0, items.length, 9999) : rows;

  const bulkActions = selectable
    ? [
        {
          label: "Send SMS",
          onClick: (selectedItems) => {
            setSmsItems(selectedItems);
            setSmsModalOpen(true);
          },
        },
      ]
    : [];

  return (
    <div className={styles.tableCard}>
      <div className={styles.tableHeader}>
        <h3 className={styles.tableTitle}>
          {getReportIcon(reportType)}
          {reportTypeToTableTitle(reportType)}
        </h3>
        <div className={styles.headerActions}>
          {isExporting ? (
            <span className={styles.exportingText}>
              <Loader2 size={16} className={styles.spinner} />
              Generating...
            </span>
          ) : PDF_EXPORT_TYPES.has(reportType) ? (
            <>
              <Button
                variant="text"
                size="small"
                onClick={() => onExport("pdf")}
              >
                Export PDF
              </Button>
              <Button
                variant="text"
                size="small"
                onClick={() => onExport("excel")}
              >
                Export Excel
              </Button>
            </>
          ) : (
            <Button
              variant="text"
              size="small"
              onClick={() => onExport("excel")}
            >
              Export
            </Button>
          )}
        </div>
      </div>
      <DataTable
        columns={getColumns(reportType)}
        data={items}
        getRowId={rowIdFn}
        isLoading={isLoading}
        selectable={selectable}
        selection={selection}
        bulkActions={bulkActions}
        rowActions={rowActions}
        emptyTitle={finderIdle ? "Select search criteria" : undefined}
        emptyText={
          finderIdle
            ? "Pick at least one condition or medication class to find prospective CDM patients."
            : undefined
        }
        search={tableSearch}
        filtersConfig={filtersConfig}
        filters={tableFilters}
        currentPage={page}
        rowsPerPage={effectiveRowsPerPage}
        totalCount={rows === "All" ? undefined : totalRecords}
        onPageChange={onPageChange}
        rowsOptions={ROWS_OPTIONS}
        rowsValue={rows}
        onRowsChange={onRowsChange}
      />
      {smsModalOpen && (
        <SendSMSModal
          reportType={reportType}
          selectedItems={smsItems}
          onClose={() => setSmsModalOpen(false)}
        />
      )}
    </div>
  );
};
